/**
 * Global Audio Processing Context
 *
 * Manages the noise-suppressed audio stream globally so it can be used
 * across voice calls, video calls, and settings previews.
 */

'use client';

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { AdvancedNoiseProcessor } from '../../lib/audio/processors/advanced-noise-processor';

interface AudioProcessingContextType {
  // Processor state
  isProcessorActive: boolean;
  noiseSuppressionLevel: 'off' | 'standard' | 'maximum';

  // Streams
  rawMicStream: MediaStream | null;
  processedStream: MediaStream | null;

  // Methods
  initializeProcessor: (level: 'off' | 'standard' | 'maximum') => Promise<void>;
  setNoiseSuppressionLevel: (level: 'off' | 'standard' | 'maximum') => Promise<void>;
  getAudioStream: () => MediaStream | null;
  cleanup: () => Promise<void>;

  // Metrics
  metrics: {
    inputLevel: number;
    outputLevel: number;
    noiseReduction: number;
    vadProbability: number;
    latency: number;
  };

  // VAD state
  isVoiceActive: boolean;
}

const AudioProcessingContext = createContext<AudioProcessingContextType | null>(null);

export function AudioProcessingProvider({ children }: { children: React.ReactNode }) {
  const [isProcessorActive, setIsProcessorActive] = useState(false);
  const [noiseSuppressionLevel, setNoiseSuppression] = useState<'off' | 'standard' | 'maximum'>('standard');
  const [rawMicStream, setRawMicStream] = useState<MediaStream | null>(null);
  const [processedStream, setProcessedStream] = useState<MediaStream | null>(null);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [metrics, setMetrics] = useState({
    inputLevel: -Infinity,
    outputLevel: -Infinity,
    noiseReduction: 0,
    vadProbability: 0,
    latency: 0
  });

  const processorRef = useRef<AdvancedNoiseProcessor | null>(null);

  /**
   * Initialize the noise processor with a specific level
   */
  const initializeProcessor = useCallback(async (level: 'off' | 'standard' | 'maximum') => {
    try {
      console.log('[AudioProcessing] Initializing processor with level:', level);

      // Get microphone stream with OPTIMAL settings for low latency
      // Based on research: disable browser noise suppression for better latency
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,          // Keep echo cancellation (doesn't affect latency much)
          noiseSuppression: false,         // CRITICAL: Disable browser NS (we use DeepFilterNet)
          autoGainControl: false,          // Disable AGC for lower latency
          sampleRate: { ideal: 48000 },    // DeepFilterNet works best at 48kHz
          channelCount: { ideal: 1 }       // Mono for lower CPU usage
        }
      });

      console.log(`[AudioProcessing] Got microphone stream:
        - Sample rate: ${stream.getAudioTracks()[0].getSettings().sampleRate}Hz
        - Channels: ${stream.getAudioTracks()[0].getSettings().channelCount}
      `);

      setRawMicStream(stream);

      if (level === 'off') {
        // Use raw mic stream directly
        setProcessedStream(stream);
        setIsProcessorActive(false);
        console.log('[AudioProcessing] Using raw mic stream (noise suppression OFF)');
        return;
      }

      // Create processor
      const aggressiveness = level === 'maximum' ? 10 : 5;
      const processor = new AdvancedNoiseProcessor({
        sampleRate: 48000,
        enableVAD: true,
        vadProbabilityThreshold: 0.5,
        vadHoldTime: 300,
        aggressiveness
      });

      // Initialize with stream
      const cleanStream = await processor.initialize(stream);

      // Set up metrics callback
      processor.setOnMetricsUpdate((newMetrics) => {
        setMetrics(newMetrics);
      });

      // Set up VAD callback
      processor.setOnVADStateChange((active) => {
        setIsVoiceActive(active);
      });

      // Start processing
      processor.start();

      // Store
      processorRef.current = processor;
      setProcessedStream(cleanStream);
      setIsProcessorActive(true);
      setNoiseSuppression(level);

      console.log('[AudioProcessing] Processor initialized successfully');
    } catch (error) {
      console.error('[AudioProcessing] Failed to initialize processor:', error);

      // FALLBACK: Use raw mic if processor initialization fails
      // This ensures audio still works even if advanced processing fails
      try {
        console.warn('[AudioProcessing] Falling back to raw microphone...');

        // Try to get raw mic stream if we don't have one
        let fallbackStream = rawMicStream;
        if (!fallbackStream) {
          fallbackStream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true }
          });
          setRawMicStream(fallbackStream);
        }

        // Use raw stream as processed stream
        setProcessedStream(fallbackStream);
        setIsProcessorActive(false);
        setNoiseSuppression('off');

        console.log('[AudioProcessing] Successfully fell back to raw mic');
      } catch (fallbackError) {
        console.error('[AudioProcessing] Even fallback failed:', fallbackError);
        throw fallbackError;
      }

      // Re-throw original error so caller knows there was an issue
      throw error;
    }
  }, [rawMicStream]);

  /**
   * Change noise suppression level on the fly
   */
  const setNoiseSuppressionLevel = useCallback(async (level: 'off' | 'standard' | 'maximum') => {
    console.log('[AudioProcessing] Changing noise suppression level to:', level);

    // Cleanup current processor
    if (processorRef.current) {
      await processorRef.current.cleanup();
      processorRef.current = null;
    }

    // Reinitialize with new level
    await initializeProcessor(level);
  }, [initializeProcessor]);

  /**
   * Get the current audio stream (processed or raw)
   */
  const getAudioStream = useCallback((): MediaStream | null => {
    return processedStream || rawMicStream;
  }, [processedStream, rawMicStream]);

  /**
   * Cleanup processor
   */
  const cleanup = useCallback(async () => {
    console.log('[AudioProcessing] Cleaning up...');

    if (processorRef.current) {
      await processorRef.current.cleanup();
      processorRef.current = null;
    }

    if (rawMicStream) {
      rawMicStream.getTracks().forEach(track => track.stop());
      setRawMicStream(null);
    }

    setProcessedStream(null);
    setIsProcessorActive(false);
  }, [rawMicStream]);

  /**
   * Cleanup on unmount only - NO auto-initialization
   *
   * IMPORTANT: Microphone access MUST be triggered by user interaction
   * (e.g., clicking a button) due to browser permissions policies.
   * Auto-requesting mic access on mount causes:
   * - "Permissions policy violation: microphone is not allowed"
   * - NotAllowedError: Permission denied
   *
   * Consumers should call initializeProcessor() in response to user action.
   */
  useEffect(() => {
    // Cleanup on unmount
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const value: AudioProcessingContextType = {
    isProcessorActive,
    noiseSuppressionLevel,
    rawMicStream,
    processedStream,
    initializeProcessor,
    setNoiseSuppressionLevel,
    getAudioStream,
    cleanup,
    metrics,
    isVoiceActive
  };

  return (
    <AudioProcessingContext.Provider value={value}>
      {children}
    </AudioProcessingContext.Provider>
  );
}

/**
 * Hook to use audio processing context
 */
export function useAudioProcessing() {
  const context = useContext(AudioProcessingContext);
  if (!context) {
    throw new Error('useAudioProcessing must be used within AudioProcessingProvider');
  }
  return context;
}
